/**
 * VendorEarnings — the vendor's money view.
 * ─────────────────────────────────────────────────────────────────────────────
 * A vendor has exactly three questions: what did I sell, what was I charged, and
 * what am I owed. This answers those and stops there. The commission rate is
 * shown as an EFFECTIVE percentage computed from the money actually taken, not as
 * the contract number, so a vendor can verify their contract is being honoured
 * rather than being asked to trust it.
 *
 * Lusuku's own margin, the delivery fee split, the payment rail cost and every
 * other vendor's numbers are absent because /api/money/vendor/:id never sends
 * them. A vendor asking for another vendor is refused outright.
 */

import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { apiFetchVendorMoneyAnalytics } from "../../api/index";
import {
  C,
  S,
  ErrorNote,
  Kpi,
  KpiRow,
  Loading,
  MoneyRow,
  SectionTitle,
  WindowPicker,
  dayLabel,
  fmtPct,
  fmtUGX,
  moneyBarOptions,
  useMoneyReport,
} from "./moneyUi";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BEARER_NOTE = {
  LUSUKU:
    "Lusuku absorbs this. It is not deducted from your payout and it is not charged to your customers.",
  CUSTOMER: "Your customers covered this on their orders. It is not deducted from your payout.",
  MIXED:
    "Part of this was covered by customers and the rest by Lusuku. None of it is deducted from your payout.",
  NONE: "No payment rail charge arose on these sales.",
};

export default function VendorEarnings({ vendorId, title }) {
  const { data, loading, error, days, setDays, refreshedAt } = useMoneyReport(
    (d) => apiFetchVendorMoneyAnalytics(vendorId, { days: d }),
    [vendorId],
    { days: 30 }
  );

  const totals = data?.totals;
  const processing = data?.paymentProcessing;
  const subjectName = data?.subject?.name;

  const trendChart = useMemo(() => {
    const t = data?.trend || [];
    const base = moneyBarOptions({ stacked: true });
    return {
      data: {
        labels: t.map((d) => dayLabel(d.day)),
        datasets: [
          {
            label: "Your payout",
            data: t.map((d) => d.payout),
            backgroundColor: C.teal,
            stack: "sales",
            maxBarThickness: 26,
          },
          {
            label: "Commission",
            data: t.map((d) => Math.max(0, d.sales - d.payout)),
            backgroundColor: C.gold,
            stack: "sales",
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const row = t[items[0]?.dataIndex];
                if (!row) return "";
                return `${row.orders} order${row.orders === 1 ? "" : "s"} · sold ${fmtUGX(
                  row.sales
                )}`;
              },
              label: (ctx) => `${ctx.dataset.label}: ${fmtUGX(ctx.parsed.y)}`,
            },
          },
        },
      },
    };
  }, [data]);

  if (!vendorId) return null;

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h4 style={S.title}>
            <i className="fas fa-wallet" style={{ color: C.teal }} />
            {title || (subjectName ? `${subjectName} earnings` : "Earnings and commission")}
          </h4>
          <div style={S.subtitle}>
            Completed orders: goods sold, commission charged and the payout due to you
            {refreshedAt ? ` · updated ${refreshedAt.toLocaleTimeString()}` : ""}
          </div>
        </div>
        <WindowPicker value={days} onChange={setDays} />
      </div>

      {loading && <Loading what="your earnings" />}
      {!loading && error && <ErrorNote>{error}</ErrorNote>}

      {!loading && !error && totals && (
        <>
          <KpiRow>
            <Kpi
              label="Goods sold"
              value={fmtUGX(totals.sales)}
              meta={`${totals.delivered} completed order${totals.delivered === 1 ? "" : "s"} of ${
                totals.orders
              } received`}
              accent={C.teal}
            />
            <Kpi
              label="Payout due"
              value={fmtUGX(totals.payout)}
              meta="On completed orders, before any agreed deductions"
              accent={C.green}
            />
            <Kpi
              label="Commission charged"
              value={fmtUGX(totals.commission)}
              meta={`${fmtPct(totals.effectiveCommissionPct)} of goods sold`}
              accent={C.gold}
            />
            <Kpi
              label="Average order value"
              value={fmtUGX(totals.averageOrderValue)}
              meta={
                totals.cancelled
                  ? `${totals.cancelled} cancelled in this window`
                  : "No cancellations in this window"
              }
              accent={C.navy}
            />
          </KpiRow>

          <SectionTitle first>Sales, day by day</SectionTitle>
          <div style={S.chartBox}>
            <Bar data={trendChart.data} options={trendChart.options} />
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <SectionTitle first>How a sale reaches you</SectionTitle>
              <MoneyRow label="Customers paid for your goods" value={fmtUGX(totals.sales)} />
              <MoneyRow
                label={`Commission at ${fmtPct(totals.effectiveCommissionPct)}`}
                value={fmtUGX(-totals.commission)}
              />
              <div
                style={{
                  ...S.row,
                  borderBottom: "none",
                  paddingTop: 10,
                  fontWeight: 800,
                  color: C.ink,
                }}
              >
                <span>Owed to you</span>
                <span>{fmtUGX(totals.payout)}</span>
              </div>
              <div style={{ ...S.muted, marginTop: 10 }}>
                The delivery fee the customer paid is separate and is not charged to you. Your
                prices are never marked up.
              </div>
            </div>
            {processing && processing.railCharge > 0 && (
              <div style={S.panel}>
                <SectionTitle first>{processing.label || "Payment processing"}</SectionTitle>
                <MoneyRow
                  label="Charged by the payment provider on your sales"
                  value={fmtUGX(processing.railCharge)}
                  sub={
                    totals.sales
                      ? fmtPct((processing.railCharge / totals.sales) * 100)
                      : null
                  }
                />
                {processing.coveredByCustomer > 0 && (
                  <MoneyRow label="Covered by customers" value={fmtUGX(processing.coveredByCustomer)} />
                )}
                {processing.borneByLusuku > 0 && (
                  <MoneyRow label="Absorbed by Lusuku" value={fmtUGX(processing.borneByLusuku)} />
                )}
                <div style={{ ...S.muted, marginTop: 10 }}>
                  {BEARER_NOTE[processing.bearer] || BEARER_NOTE.NONE} Mobile money and card
                  take a percentage of everything a customer pays, including your goods, which
                  is why a share of it lands against your sales.
                </div>
              </div>
            )}

            <div style={S.panel}>
              <SectionTitle first>Fulfilment</SectionTitle>
              <MoneyRow label="Orders received" value={String(totals.orders)} />
              <MoneyRow label="Completed" value={String(totals.delivered)} />
              <MoneyRow label="In progress" value={String(totals.open ?? Math.max(0, totals.orders - totals.delivered - totals.cancelled))} />
              <MoneyRow label="Cancelled or rejected" value={String(totals.cancelled)} />
              <MoneyRow
                label="Fulfilment rate"
                value={
                  totals.orders ? fmtPct((totals.delivered / totals.orders) * 100, 0) : "n/a"
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
